import { useRouter } from 'next/router';
import { useViewTransition } from '@watershed/shared-frontend/hooks/useViewTransition';
import { useFeatureFlag } from './FeatureFlag';
import { Flags } from '@watershed/constants/flags';

interface RouteVersionConfig {
  routePattern: string; // e.g., '/logs', '/settings' (trailing slash optional)
  featureFlag: Flags;
}

const getVersionSegment = (routePattern: string): string =>
  routePattern.endsWith('/') ? 'v2/' : '/v2';

export function transformToV2Route(baseRoute: string, routePattern: string) {
  const versionSegment = getVersionSegment(routePattern);
  return baseRoute.replace(routePattern, `${routePattern}${versionSegment}`);
}

/**
 * Transforms a route to its v2 version and provides navigation utilities
 * Automatically handles exact matches vs. routes with additional segments:
 * - /logs → /logs/v2
 * - /logs/audit → /logs/v2/audit
 */
export function useRedirectForVersion(config: RouteVersionConfig) {
  const router = useRouter();
  const startViewTransition = useViewTransition();
  const isFeatureEnabled = useFeatureFlag(config.featureFlag);

  const getV2Route = (baseRoute: string) => {
    return transformToV2Route(baseRoute, config.routePattern);
  };

  const getNonV2Route = (baseRoute: string) => {
    const versionSegment = getVersionSegment(config.routePattern);
    return baseRoute.replace(
      `${config.routePattern}${versionSegment}`,
      config.routePattern
    );
  };

  const redirectToV2 = async (baseRoute: string) => {
    // If feature flag is disabled, redirect to non-v2 version instead
    const targetRoute = isFeatureEnabled
      ? getV2Route(baseRoute)
      : getNonV2Route(getV2Route(baseRoute)); // Convert to v2 then back to ensure consistent behavior

    if (startViewTransition) {
      startViewTransition(() => {
        void router.push(targetRoute);
      });
    } else {
      await router.push(targetRoute);
    }
  };

  return { redirectToV2, getV2Route };
}

const ROUTE_CONFIGS: Record<string, RouteVersionConfig> = {
  settings: {
    routePattern: '/settings',
    featureFlag: Flags.EntFoundSettingsV2,
  },
  logs: {
    routePattern: '/logs',
    featureFlag: Flags.AuditLogV2,
  },
};

// hooks for convenience
export const useSettingsV2Redirect = () =>
  useRedirectForVersion(ROUTE_CONFIGS.settings);
export const useLogsV2Redirect = () =>
  useRedirectForVersion(ROUTE_CONFIGS.logs);
